import React, { useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { Badge, Modal } from "react-bootstrap";

import axiosInstance from "../../../../../services/AxiosInstance";
import { notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";

const CalendarioReservasVigilante = () => {
  const calendarRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchEvents = async (fetchInfo, successCallback, failureCallback) => {
    try {
      const params = new URLSearchParams({
        start: fetchInfo.startStr,
        end: fetchInfo.endStr,
      });
      const response = await axiosInstance.get(
        `/api/v3/vigilancia/zonas-sociales/calendario/?${params.toString()}`
      );
      successCallback(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      notifyError("Error al cargar el calendario de reservas");
      failureCallback(error);
    }
  };

  const handleEventClick = (clickInfo) => {
    const eventData = {
      ...clickInfo.event.extendedProps,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
    };
    setSelectedEvent(eventData);
    setShowModal(true);
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      confirmada: <Badge bg="success">Confirmada</Badge>,
      pendiente: <Badge bg="warning">Pendiente</Badge>,
      cancelada: <Badge bg="danger">Cancelada</Badge>,
      incumplida: <Badge bg="secondary">Incumplida</Badge>,
    };
    return estados[estado] || <Badge bg="secondary">{estado}</Badge>;
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "full",
      timeStyle: "short",
    }).format(date);
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">Calendario de Reservas</h4>
        </div>
        <div className="card-body">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            locale={esLocale}
            events={fetchEvents}
            eventClick={handleEventClick}
            height="auto"
            eventDisplay="block"
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              meridiem: false,
            }}
          />
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalle de Reserva</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <div className="vstack gap-4">
              <section>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0">Información General</h5>
                  {getEstadoBadge(selectedEvent.estado)}
                </div>
                <hr />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Título</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.title || "Sin título"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Zona Social</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.zona_nombre || "No disponible"}
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Descripción</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.descripcion || "Sin descripción"}
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h5>Responsable y Reglas</h5>
                <hr />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Responsable</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.zona_responsable || "No disponible"}
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Reglas de Uso</label>
                    <div 
                      className="form-control-plaintext" 
                      dangerouslySetInnerHTML={{ 
                        __html: selectedEvent.zona_reglas_uso || "Sin reglas definidas" 
                      }} 
                    />
                  </div>
                </div>
              </section>

              <section>
                <h5>Horario</h5>
                <hr />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Inicio</label>
                    <div className="form-control-plaintext">
                      {formatDateTime(selectedEvent.start)}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Fin</label>
                    <div className="form-control-plaintext">
                      {formatDateTime(selectedEvent.end)}
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h5>Residente e Inmueble</h5>
                <hr />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Residente</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.residente_nombre || "No disponible"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Inmueble</label>
                    <div className="form-control-plaintext">
                      {selectedEvent.inmueble || "No disponible"}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowModal(false)}
          >
            Cerrar
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CalendarioReservasVigilante;
